import { Typography, Empty } from 'antd';
import { groupBy } from 'lodash';
import { CustomerCard } from './CustomerCard';
import { TBoardViewProps } from '@/types/TSmartCRM';
import {
	generateGroupOptions,
	getCustomerGroupValue,
	filterReservedFields,
} from '@/utils/SmartCRM/fieldUtils';
import { useState } from 'react';
import { CustomerModal } from '../LeadDetails/CustomerModal';

const { Title, Text } = Typography;

interface EnhancedBoardViewProps extends TBoardViewProps {
	isFilterView?: boolean;
	filterViewName?: string;
	customGroupNames?: string[];
	responseGroups?: Record<string, any[]>;
}

export const BoardView = ({
	customers,
	fields,
	groupBy: groupByField,
	onCustomerAction,
	loading = false,
	deleteCustomer,
	isFilterView = false,
	filterViewName,
	customGroupNames,
	responseGroups,
}: EnhancedBoardViewProps) => {
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleCustomerClick = (customer: any, event: React.MouseEvent) => {
		if (event.defaultPrevented) {
			return;
		}
		setSelectedCustomer(customer);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedCustomer(null);
	};

	const enhancedOnCustomerAction = (action: string, customer: any, event?: React.MouseEvent) => {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (action === 'view') {
			handleCustomerClick(customer, event || ({} as React.MouseEvent));
		} else if (onCustomerAction) {
			onCustomerAction(action, customer);
		}
	};

	if (loading) {
		return (
			<div className="flex gap-4 overflow-x-auto p-6 h-[calc(100vh-240px)]">
				{[1, 2, 3, 4].map((index) => (
					<div key={index} className="flex-shrink-0 w-80">
						<div className="bg-gray-50 rounded-lg h-full flex flex-col">
							<div className="p-3 border-b border-gray-200">
								<div className="flex items-center justify-between">
									<div className="h-5 bg-gray-200 rounded w-24 animate-pulse" />
									<div className="h-6 w-8 bg-gray-200 rounded-full animate-pulse" />
								</div>
							</div>
							<div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
								{[1, 2, 3, 4].map((cardIndex) => (
									<div key={cardIndex} className="bg-white rounded-lg border p-4 space-y-3">
										<div className="flex items-center justify-between">
											<div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
											<div className="h-6 w-6 bg-gray-200 rounded animate-pulse" />
										</div>
										<div className="space-y-2">
											<div className="h-3 bg-gray-200 rounded w-full animate-pulse" />
											<div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse" />
										</div>
										<div className="flex gap-2">
											<div className="h-6 bg-gray-200 rounded w-16 animate-pulse" />
											<div className="h-6 bg-gray-200 rounded w-20 animate-pulse" />
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				))}
			</div>
		);
	}

	if (!isFilterView) {
		const userFields = filterReservedFields(fields);

		if (userFields.length === 0) {
			return (
				<div className="flex justify-center items-center h-64">
					<Empty
						description={
							<div className="text-center">
								<p className="text-gray-500 mb-2">Board view requires custom fields</p>
								<p className="text-sm text-gray-400">
									Create custom fields to organize your customers in board view
								</p>
							</div>
						}
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				</div>
			);
		}

		const groupField = userFields.find((f) => f.internalKey === groupByField);

		if (!groupField) {
			return (
				<div className="flex justify-center items-center h-64">
					<Empty
						description="Please select a valid field to group by"
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				</div>
			);
		}
	}

	if (isFilterView && filterViewName) {
		const groupField = fields.find((f) => f.internalKey === groupByField);

		if (!groupField || !customGroupNames || customGroupNames.length === 0) {
			const displayName = customGroupNames?.[0] || filterViewName;

			return (
				<div className="flex gap-4 overflow-x-auto p-6 h-[calc(100vh-240px)]">
					<div className="flex-shrink-0 w-80">
						<div className="bg-gray-50 rounded-lg h-full flex flex-col">
							<div className="p-3 border-b border-gray-200">
								<div className="flex items-center justify-between">
									<Title level={5} className="m-0 text-gray-800 truncate">
										<span className="text-orange-600">{displayName}</span>
									</Title>
									<div className="px-2 py-1 rounded-full text-xs font-medium ml-2 bg-orange-100 text-orange-600">
										{customers.length}
									</div>
								</div>
							</div>

							<div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
								{customers.length === 0 ? (
									<div className="flex justify-center items-center h-32 text-gray-500">
										<Text type="secondary">No customers match this filter</Text>
									</div>
								) : (
									customers.map((customer) => (
										<CustomerCard
											key={customer.id}
											customer={customer}
											fields={fields}
											onAction={onCustomerAction}
											deleteCustomer={deleteCustomer}
										/>
									))
								)}
							</div>
						</div>
					</div>
				</div>
			);
		}

		if (customGroupNames.length > 1 && responseGroups) {
			return (
				<div className="flex gap-4 overflow-x-auto p-6 h-[calc(100vh-300px)]">
					{customGroupNames.map((groupName, index) => {
						const groupKey = groupName.toLowerCase().replace(/\s+/g, '_');
						const fallbackKey = `group_${index + 1}`;
						const exactGroupCustomers = responseGroups[groupName];
						const processedGroupCustomers = responseGroups[groupKey];
						const fallbackGroupCustomers = responseGroups[fallbackKey];
						const groupCustomers =
							exactGroupCustomers || processedGroupCustomers || fallbackGroupCustomers || [];

						return (
							<div key={`${groupName}-${index}`} className="flex-shrink-0 w-80">
								<div className="bg-gray-50 rounded-lg h-full flex flex-col">
									<div className="p-3 border-b border-gray-200">
										<div className="flex items-center justify-between">
											<Title level={5} className="m-0 text-gray-800 truncate">
												<span className="text-orange-600 font-medium">{groupName}</span>
											</Title>
											<div className="px-2 py-1 rounded-full text-xs font-medium ml-2 bg-orange-100 text-orange-600">
												{groupCustomers.length}
											</div>
										</div>
									</div>

									<div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
										{groupCustomers.length === 0 ? (
											<div className="flex justify-center items-center h-32 text-gray-500">
												<Text type="secondary">No customers</Text>
											</div>
										) : (
											groupCustomers.map((customer) => (
												<CustomerCard
													key={customer.id}
													customer={customer}
													fields={fields}
													onAction={onCustomerAction}
													deleteCustomer={deleteCustomer}
												/>
											))
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			);
		}

		return (
			<div className="flex gap-4 overflow-x-auto p-6 h-[calc(100vh-240px)]">
				{customGroupNames.map((groupName, index) => {
					const groupCustomers = customers;

					return (
						<div key={`${groupName}-${index}`} className="flex-shrink-0 w-80">
							<div className="bg-gray-50 rounded-lg h-full flex flex-col">
								<div className="p-3 border-b border-gray-200">
									<div className="flex items-center justify-between">
										<Title level={5} className="m-0 text-gray-800 truncate">
											<span className="text-orange-600 font-medium">{groupName}</span>
										</Title>
										<div className="px-2 py-1 rounded-full text-xs font-medium ml-2 bg-orange-100 text-orange-600">
											{groupCustomers.length}
										</div>
									</div>
								</div>

								<div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
									{groupCustomers.length === 0 ? (
										<div className="flex justify-center items-center h-32 text-gray-500">
											<Text type="secondary">No customers</Text>
										</div>
									) : (
										groupCustomers.map((customer) => (
											<CustomerCard
												key={customer.id}
												customer={customer}
												fields={fields}
												onAction={onCustomerAction}
												deleteCustomer={deleteCustomer}
											/>
										))
									)}
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	const groupField = fields.find((f) => f.internalKey === groupByField);

	if (!groupField) {
		return (
			<div className="flex justify-center items-center h-64">
				<Empty description="Selected group field not found" />
			</div>
		);
	}

	const groupedCustomers = groupBy(customers, (customer: any) =>
		getCustomerGroupValue(customer, groupField),
	);

	const fieldOptions = groupField.options || [];
	const hasUnassigned = groupedCustomers.Unassigned && groupedCustomers.Unassigned.length > 0;

	const allGroupOptions = generateGroupOptions(
		fieldOptions,
		groupField.fieldType,
		groupField.fieldName,
		groupedCustomers,
		hasUnassigned,
	);

	const getGroupDisplayName = (groupValue: string) => {
		return groupValue;
	};

	return (
		<>
			<div className="flex gap-4 overflow-x-auto p-6 h-[calc(100vh-240px)]">
				{allGroupOptions.map((groupValue) => {
					const groupCustomers = groupedCustomers[groupValue] || [];
					const displayName = getGroupDisplayName(groupValue);

					return (
						<div key={groupValue} className="flex-shrink-0 w-80">
							<div className="bg-gray-50 rounded-lg h-full flex flex-col">
								<div className="p-3 border-b border-gray-200">
									<div className="flex items-center justify-between">
										<Title level={5} className="m-0 text-gray-800 truncate">
											{groupValue === 'Unassigned' ? (
												<span className="text-gray-500 italic">{displayName}</span>
											) : (
												<span>{displayName}</span>
											)}
										</Title>
										<div
											className={`px-2 py-1 rounded-full text-xs font-medium ml-2 ${
												groupValue === 'Unassigned'
													? 'bg-gray-100 text-gray-600'
													: 'bg-blue-100 text-blue-600'
											}`}
										>
											{groupCustomers.length}
										</div>
									</div>
								</div>

								<div className="flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
									{groupCustomers.length === 0 ? (
										<div className="flex justify-center items-center h-32 text-gray-500">
											<Text type="secondary">No customers</Text>
										</div>
									) : (
										groupCustomers.map((customer) => (
											<div
												key={customer.id}
												onClick={(e) => handleCustomerClick(customer, e)}
												className="cursor-pointer"
											>
												<CustomerCard
													customer={customer}
													fields={fields}
													onAction={enhancedOnCustomerAction}
													deleteCustomer={deleteCustomer}
												/>
											</div>
										))
									)}
								</div>
							</div>
						</div>
					);
				})}
			</div>
			<CustomerModal customer={selectedCustomer} isOpen={isModalOpen} onClose={handleCloseModal} />
		</>
	);
};
